import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Params } from '@angular/router';
import { Observable, of, shareReplay, throwError } from 'rxjs';

import { environment } from '@www-src/environments/environment';
import { ContentCard, ContentEntry, ContentIndex, ContentSite } from './content.models';

export const DEFAULT_LANGUAGE = 'en';

// Reads the help center and blog data: from disk while prerendering, then from /content/data.
@Injectable({
  providedIn: 'root'
})
export class ContentService {
  private http = inject(HttpClient);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  private cache = new Map<string, Observable<unknown>>();

  public site(): Observable<ContentSite> {
    return this.load<ContentSite>('site.json');
  }

  public index(language: string): Observable<ContentIndex> {
    return this.load<ContentIndex>(`${language}/index.json`);
  }

  public entry(language: string, id: string): Observable<ContentEntry> {
    return this.load<ContentEntry>(`${language}/${id}.json`);
  }

  public card(index: ContentIndex, id: string): ContentCard | undefined {
    return index.entries.find(entry => entry.id === id);
  }

  public categoryLabel(index: ContentIndex, card: ContentCard): string {
    const categories = card.kind === 'help' ? index.helpCategories : index.blogCategories;

    return categories.find(category => category.id === card.category)?.label ?? '';
  }

  // The page's language, from the address's first segment.
  public languageOf(params: Params): string {
    return params['locale'] ?? DEFAULT_LANGUAGE;
  }

  // An entry id ('help/a/b') or a section ('blog') as router commands, with the language
  // in front for every language but English, as the other pages link.
  public link(language: string, path: string): string[] {
    const segments = path.split('/');

    return language === DEFAULT_LANGUAGE ? ['/', ...segments] : ['/', language, ...segments];
  }

  // The same address as a path: '/help/a/b', '/de/help/a/b'.
  public url(language: string, path: string): string {
    return `/${this.link(language, path).slice(1).join('/')}`;
  }

  private load<T>(file: string): Observable<T> {
    if (!this.cache.has(file)) {
      this.cache.set(file, this.transferOrFetch<T>(file).pipe(shareReplay(1)));
    }

    return this.cache.get(file) as Observable<T>;
  }

  private transferOrFetch<T>(file: string): Observable<T> {
    const stateKey = makeStateKey<T>(`content-${file}`);

    if (isPlatformBrowser(this.platformId)) {
      if (this.transferState.hasKey(stateKey)) {
        const cached = this.transferState.get(stateKey, null as T);

        this.transferState.remove(stateKey);

        return of(cached);
      }

      return this.http.get<T>(`/content/data/${file}`);
    }

    // Read at once, as the server translation loader does, so prerendering never saves a
    // page before its content is there.
    try {
      const { loadContentFileFs } = require('../../prerendering/load-content-file');
      const value = JSON.parse(loadContentFileFs(environment.ENVIRONMENT, file)) as T;

      this.transferState.set(stateKey, value);

      return of(value);
    } catch (error) {
      return throwError(() => error);
    }
  }
}
